import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Building2, CheckCircle, XCircle, Loader2 } from "lucide-react";

interface ValidatedGym {
  id: string;
  name: string;
  logo_url: string | null;
}

interface GymCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidGym: (gym: ValidatedGym | null) => void;
  error?: string;
}

export const GymCodeInput = ({ value, onChange, onValidGym, error }: GymCodeInputProps) => {
  const [isChecking, setIsChecking] = useState(false);
  const [validatedGym, setValidatedGym] = useState<ValidatedGym | null>(null);
  const [localError, setLocalError] = useState("");
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkGymCode = useCallback(async (code: string) => {
    if (code.length !== 6) {
      setValidatedGym(null);
      onValidGym(null);
      if (code.length > 0 && code.length < 6) {
        setLocalError("El código debe tener 6 caracteres");
      } else {
        setLocalError("");
      }
      return;
    }

    setIsChecking(true);
    setLocalError("");

    try {
      const { data: gym, error: queryError } = await supabase
        .from("gyms")
        .select("id, name, logo_url")
        .eq("code", code.toLowerCase())
        .maybeSingle();

      if (gym && !queryError) {
        setValidatedGym(gym);
        onValidGym(gym);
        setLocalError("");
      } else {
        setValidatedGym(null);
        onValidGym(null);
        setLocalError("Código de gimnasio no encontrado");
      }
    } catch (err) {
      console.error("Error checking gym code:", err);
      setLocalError("Error al verificar el código");
      setValidatedGym(null);
      onValidGym(null);
    } finally {
      setIsChecking(false);
    }
  }, [onValidGym]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.slice(0, 6);
    onChange(newValue);
    setLocalError("");
    setValidatedGym(null);
    onValidGym(null);

    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    if (newValue.length === 6) {
      checkTimeoutRef.current = setTimeout(() => {
        checkGymCode(newValue);
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, []);

  const displayError = error || localError;

  return (
    <div className="space-y-2">
      <Label htmlFor="gymCode" className="text-white font-medium">
        Código de gimnasio (opcional)
      </Label>
      <div className="relative">
        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 z-10" />
        <Input
          id="gymCode"
          type="text"
          value={value}
          onChange={handleChange}
          placeholder="ABC123"
          maxLength={6}
          className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 pr-10 transition-all duration-200 focus:border-white/40"
        />
        {isChecking && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 text-[#00C1D6] animate-spin" />
          </div>
        )}
        {!isChecking && validatedGym && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <CheckCircle className="h-4 w-4 text-green-400" />
          </div>
        )}
        {!isChecking && displayError && value.length > 0 && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <XCircle className="h-4 w-4 text-red-400" />
          </div>
        )}
      </div>
      
      {validatedGym && (
        <div className="flex items-center gap-2 text-green-400 text-sm bg-green-400/10 p-2 rounded-lg">
          {validatedGym.logo_url ? (
            <img 
              src={validatedGym.logo_url} 
              alt={validatedGym.name}
              className="h-8 w-8 rounded-md object-cover flex-shrink-0"
            />
          ) : (
            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          <span>Gimnasio encontrado: <strong>{validatedGym.name}</strong></span>
        </div>
      )}
      
      {displayError && (
        <p className="text-red-400 text-sm">{displayError}</p>
      )}
    </div>
  );
};
